import type { ContractRouterClient } from "@orpc/contract";
import type { JsonifiedClient } from "@orpc/openapi-client";
import { createORPCClient } from "@orpc/client";
import { OpenAPILink } from "@orpc/openapi-client/fetch";
import * as contractModule from "@jagad/contracts/contract";
import { TonWalletUtils } from "./secrets";
import { NotLoggedInError } from "./error";

const BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";
const { contract } = contractModule;

let refreshPromise: Promise<void> | null = null;

function refreshTokenOnce(): Promise<void> {
  refreshPromise ??= TonWalletUtils.refreshToken().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

async function getToken(): Promise<string | null> {
  try {
    return await TonWalletUtils.getValidToken();
  } catch (error) {
    if (error instanceof NotLoggedInError) {
      if (await TonWalletUtils.hasWallet()) {
        await refreshTokenOnce();
        return await TonWalletUtils.getValidToken();
      }
    }
    throw error;
  }
}

function isFormDataBody(body: BodyInit | null | undefined): body is FormData {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

function prepareRequestInit(
  requestInit?: RequestInit,
  token?: string | null
): RequestInit | undefined {
  const headers = new Headers(requestInit?.headers);

  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }

  if (!headers.has("X-Client-Type")) {
    headers.set("X-Client-Type", "telegram-mini-app");
  }

  if (isFormDataBody(requestInit?.body)) {
    headers.delete("Content-Type");
  } else if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return {
    ...(requestInit ?? {}),
    headers,
  };
}

const link = new OpenAPILink(contract, {
  url: BASE_URL,
  headers: () => ({
    Accept: "application/json",
    "Content-Type": "application/json",
    "X-Client-Type": "telegram-mini-app",
  }),
  fetch: async (url, init) => {
    const requestInit = init as RequestInit | undefined;
    const token = await getToken().catch(() => null);
    let res = await fetch(url, prepareRequestInit(requestInit, token));

    // Auto-refresh on 401
    if (res.status === 401) {
      try {
        await refreshTokenOnce();
        const newToken = await getToken();
        res = await fetch(url, prepareRequestInit(requestInit, newToken));
      } catch {
        // Refresh failed, return original 401 response
      }
    }

    return res;
  },
});

export const api: JsonifiedClient<ContractRouterClient<typeof contract>> =
  createORPCClient(link);

// Re-export for type inference
export type Api = typeof api;
